import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import { uploadDocument, startAudit } from '../services/api';
import { useAuth } from '../context/AuthContext';

export default function Upload() {
  const { user } = useAuth();
  const isConsultant = user?.accountType === 'consultant';

  // Each file entry: { file, clientAgencyName, patientName, patientRef }
  const [fileEntries, setFileEntries] = useState([]);
  const [error, setError] = useState('');
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState([]); // per-file progress: { status, message }
  const navigate = useNavigate();

  const onDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles.length === 0) return;
    setError('');
    const newEntries = acceptedFiles.map(file => ({
      file,
      // Agency accounts: auto-fill their own agency name. Consultants: leave blank so they type the client agency.
      clientAgencyName: isConsultant ? '' : (user?.agencyName || ''),
      patientName: '',
      patientRef: '',
    }));
    setFileEntries(prev => [...prev, ...newEntries]);
  }, [isConsultant, user?.agencyName]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'text/csv': ['.csv'],
    },
    maxSize: 50 * 1024 * 1024,
  });

  const updateEntry = (index, field, value) => {
    setFileEntries(prev => prev.map((entry, i) =>
      i === index ? { ...entry, [field]: value } : entry
    ));
  };

  const removeEntry = (index) => {
    setFileEntries(prev => prev.filter((_, i) => i !== index));
  };

  const handleUploadAll = async () => {
    if (fileEntries.length === 0) return setError('Please add at least one file');

    // Consultants must fill in agency name for each file
    if (isConsultant) {
      const missing = fileEntries.some(e => !e.clientAgencyName.trim());
      if (missing) return setError('Please enter a Client Agency Name for every file');
    }

    setError('');
    setProcessing(true);
    setProgress(fileEntries.map(() => ({ status: 'pending', message: 'Waiting...' })));

    const auditIds = [];

    for (let i = 0; i < fileEntries.length; i++) {
      const entry = fileEntries[i];

      // Upload step
      setProgress(prev => prev.map((p, idx) =>
        idx === i ? { status: 'uploading', message: 'Uploading...' } : p
      ));

      try {
        const formData = new FormData();
        formData.append('document', entry.file);
        if (entry.clientAgencyName) formData.append('clientAgencyName', entry.clientAgencyName);
        if (entry.patientName) formData.append('patientName', entry.patientName);
        if (entry.patientRef) formData.append('patientReference', entry.patientRef);

        const uploadRes = await uploadDocument(formData);
        const documentId = uploadRes.data.document.id;

        // Audit step
        setProgress(prev => prev.map((p, idx) =>
          idx === i ? { status: 'auditing', message: 'Running AI Audit...' } : p
        ));

        const auditRes = await startAudit({
          documentId,
          clientAgencyName: entry.clientAgencyName,
          patientName: entry.patientName,
          patientReference: entry.patientRef,
        });

        auditIds.push(auditRes.data.auditId);

        setProgress(prev => prev.map((p, idx) =>
          idx === i ? { status: 'done', message: 'Audit started!' } : p
        ));
      } catch (err) {
        setProgress(prev => prev.map((p, idx) =>
          idx === i ? { status: 'error', message: err.response?.data?.error || 'Failed' } : p
        ));
      }
    }

    // Navigate to the first completed audit (or audit history if multiple)
    setTimeout(() => {
      if (auditIds.length === 1) {
        navigate(`/audits/${auditIds[0]}`);
      } else if (auditIds.length > 1) {
        navigate('/audits');
      }
      setProcessing(false);
    }, 2000);
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <h1 className="text-2xl font-bold text-slate-800 mb-2">Upload & Audit</h1>
      <p className="text-gray-500 mb-8">
        {isConsultant
          ? 'Upload client agency documents for AI-powered audit'
          : 'Upload OASIS assessments or clinical charts for AI-powered audit'}
      </p>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">{error}</div>
      )}

      {/* Dropzone */}
      <div {...getRootProps()}
        className={`border-2 border-dashed rounded-xl p-10 text-center cursor-pointer transition
          ${isDragActive ? 'border-blue-500 bg-blue-50' : fileEntries.length > 0 ? 'border-green-400 bg-green-50' : 'border-gray-300 hover:border-blue-400 hover:bg-gray-50'}`}>
        <input {...getInputProps()} />
        <div>
          <div className="text-4xl mb-2">{fileEntries.length > 0 ? '\u{1F4C1}' : '\u{1F4E8}'}</div>
          <p className="font-semibold text-slate-700">
            {fileEntries.length > 0
              ? `${fileEntries.length} file(s) added — drop more or click to add`
              : 'Drop your documents here, or click to select'}
          </p>
          <p className="text-sm text-gray-500 mt-1">Supports PDF, DOCX, and CSV — up to 50MB each — select multiple files at once</p>
        </div>
      </div>

      {/* File List with Per-File Fields */}
      {fileEntries.length > 0 && (
        <div className="mt-6 space-y-4">
          {fileEntries.map((entry, idx) => (
            <div key={idx} className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center space-x-3">
                  <span className="text-2xl">{'\u{1F4C4}'}</span>
                  <div>
                    <p className="font-semibold text-slate-700 text-sm">{entry.file.name}</p>
                    <p className="text-xs text-gray-400">{(entry.file.size / 1024 / 1024).toFixed(2)} MB</p>
                  </div>
                </div>
                {!processing && (
                  <button onClick={() => removeEntry(idx)}
                    className="text-red-400 hover:text-red-600 text-sm font-medium">Remove</button>
                )}
                {processing && progress[idx] && (
                  <span className={`text-xs font-semibold px-2 py-1 rounded-full ${
                    progress[idx].status === 'done' ? 'bg-green-100 text-green-700' :
                    progress[idx].status === 'error' ? 'bg-red-100 text-red-700' :
                    progress[idx].status === 'pending' ? 'bg-gray-100 text-gray-500' :
                    'bg-blue-100 text-blue-700 animate-pulse'
                  }`}>
                    {progress[idx].message}
                  </span>
                )}
              </div>

              {!processing && (
                <div className={`grid grid-cols-1 ${isConsultant ? 'md:grid-cols-3' : 'md:grid-cols-2'} gap-3`}>
                  {/* Only show agency name field for consultants — agencies already have it auto-filled */}
                  {isConsultant && (
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">Client Agency Name *</label>
                      <input value={entry.clientAgencyName}
                        onChange={(e) => updateEntry(idx, 'clientAgencyName', e.target.value)}
                        placeholder="e.g. Sunrise Home Health"
                        className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                    </div>
                  )}
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Patient Full Name</label>
                    <input value={entry.patientName}
                      onChange={(e) => updateEntry(idx, 'patientName', e.target.value)}
                      placeholder="e.g. John Smith"
                      className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Chart Ref / MRN (optional)</label>
                    <input value={entry.patientRef}
                      onChange={(e) => updateEntry(idx, 'patientRef', e.target.value)}
                      placeholder="e.g. MRN-12345"
                      className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Bulk Apply Agency Name — only for consultants with multiple files */}
      {isConsultant && fileEntries.length > 1 && !processing && (
        <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center gap-3">
          <span className="text-xs text-blue-600 font-medium whitespace-nowrap">Apply to all:</span>
          <input
            placeholder="Set client agency name for all files"
            className="flex-1 px-3 py-1.5 text-sm border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && e.target.value.trim()) {
                const val = e.target.value.trim();
                setFileEntries(prev => prev.map(entry => ({ ...entry, clientAgencyName: val })));
                e.target.value = '';
              }
            }}
          />
          <span className="text-xs text-blue-400">Press Enter to apply</span>
        </div>
      )}

      {/* Submit Button */}
      <button onClick={handleUploadAll}
        disabled={fileEntries.length === 0 || processing}
        className="mt-6 w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed">
        {processing
          ? `Processing ${fileEntries.length} file(s)...`
          : `Upload & Start Audit${fileEntries.length > 1 ? ` (${fileEntries.length} files)` : ''}`}
      </button>
    </div>
  );
}
